
import { supabase } from '@/integrations/supabase/client';
import { ProcurementItem, CreateProcurementItemInput } from '@/types/database.types';

/**
 * Create multiple procurement items for a procurement
 */
export const createProcurementItems = async (
  procurementId: string, 
  items: CreateProcurementItemInput[]
): Promise<ProcurementItem[]> => {
  console.log(`Creating ${items.length} items for procurement ${procurementId}`);
  
  const itemsToInsert = items.map(item => ({
    procurement_id: procurementId,
    product_id: item.product_id,
    product_name: item.product_name,
    price_per_unit: item.price_per_unit,
    quantity: item.quantity,
    quality_grade: item.quality_grade,
    notes: item.notes
  }));

  const { data, error } = await supabase
    .from('procurement_items')
    .insert(itemsToInsert)
    .select();

  if (error) {
    console.error("Error creating procurement items:", error);
    throw error;
  }

  console.log(`Successfully created ${data.length} procurement items`);
  return data as ProcurementItem[];
};

/**
 * Get all items for a procurement
 */
export const getProcurementItems = async (procurementId: string): Promise<ProcurementItem[]> => {
  console.log(`Fetching items for procurement ${procurementId}`);
  
  const { data, error } = await supabase
    .from('procurement_items')
    .select('*')
    .eq('procurement_id', procurementId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error("Error fetching procurement items:", error);
    throw error;
  }

  console.log(`Found ${data.length} items for procurement ${procurementId}`);
  return data as ProcurementItem[];
};

/**
 * Update a procurement item
 */
export const updateProcurementItem = async (
  itemId: string, 
  updates: Partial<CreateProcurementItemInput>
): Promise<ProcurementItem> => {
  console.log(`Updating procurement item ${itemId}`);
  
  const { data, error } = await supabase
    .from('procurement_items')
    .update(updates)
    .eq('id', itemId)
    .select()
    .single();

  if (error) {
    console.error("Error updating procurement item:", error);
    throw error;
  }

  console.log(`Successfully updated procurement item ${itemId}`);
  return data as ProcurementItem;
};

/**
 * Delete a procurement item
 */
export const deleteProcurementItem = async (itemId: string): Promise<void> => {
  console.log(`Deleting procurement item ${itemId}`);
  
  const { error } = await supabase
    .from('procurement_items')
    .delete()
    .eq('id', itemId);

  if (error) {
    console.error("Error deleting procurement item:", error);
    throw error;
  }

  console.log(`Successfully deleted procurement item ${itemId}`);
};
